import {
  type DayRange,
  formatBRL,
  LEAD_STATUS_LABELS,
  LOST_REASON_LABELS,
  LOST_REASONS,
  type LostReason,
  PLAN_CODES,
  PLANS,
  type PlanCode,
} from "@byfluent/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  Chips,
  CopyButton,
  LinkButton,
  Loading,
  Modal,
  PageHeader,
  SelectInput,
  TextArea,
  TextInput,
} from "../../components/ui";
import { WeekGrid } from "../../components/WeekGrid";
import { api, errorMessage } from "../../lib/api";
import { dateTimeBR, formatBrPhone, whatsappUrl } from "../../lib/format";
import type { LeadDetailData, LeadLink } from "../../lib/types";
import { STATUS_COLOR } from "./Leads";

const LINK_STATUS: Record<
  LeadLink["status"],
  { label: string; color: "blue" | "gold" | "violet" | "green" | "gray" }
> = {
  pending: { label: "Enviado", color: "blue" },
  opened: { label: "Aberto", color: "violet" },
  filled: { label: "Preenchido", color: "gold" },
  paid: { label: "Pago", color: "green" },
  expired: { label: "Expirado", color: "gray" },
};

const EVENT_LABELS: Record<string, string> = {
  "lead.clicked": "Clique no anúncio",
  "lead.clicked_again": "Clicou de novo",
  "lead.created_manually": "Cadastrado manualmente",
  "lead.updated": "Dados atualizados",
  "lead.status_changed": "Mudou de estado",
  "lead.lost": "Marcado como perdido",
  "lead.link_generated": "Link de matrícula gerado",
  "lead.slot_changed": "Horário trocado",
  "lead.no_reply_marked": "Sem resposta há 72 h",
  "lead.won": "Primeiro pagamento confirmado",
};

export function LeadDetail() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const [generating, setGenerating] = useState(false);
  const [losing, setLosing] = useState(false);
  const lead = useQuery({
    queryKey: ["lead", id],
    queryFn: () => api.get<LeadDetailData>(`/v1/leads/${id}`),
    // Status do link em tempo real enquanto a tela está aberta.
    refetchInterval: 15_000,
  });
  const [form, setForm] = useState({ name: "", phone: "", notes: "", planCode: "" });
  // biome-ignore lint/correctness/useExhaustiveDependencies: o formulário só volta ao valor salvo quando o lead muda, não a cada consulta de 15 s.
  useEffect(() => {
    if (lead.data) {
      setForm({
        name: lead.data.name ?? "",
        phone: lead.data.phone ? formatBrPhone(lead.data.phone) : "",
        notes: lead.data.notes ?? "",
        planCode: lead.data.planCode ?? "IND-1H",
      });
    }
  }, [lead.data?.id, lead.data?.updatedAt]);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/v1/leads/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lead", id] });
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
  });

  if (lead.isLoading) return <Loading />;
  if (lead.error || !lead.data) return <Alert kind="risk">{errorMessage(lead.error)}</Alert>;
  const l = lead.data;
  const activeLink = l.links.find(
    (k) => k.purpose === "primary" && ["pending", "opened", "filled"].includes(k.status),
  );

  return (
    <>
      <PageHeader
        title={l.name ?? `Lead ${l.code}`}
        subtitle={
          <span className="row">
            <span className="code-tag">{l.code}</span>
            <Badge color={STATUS_COLOR[l.status]}>{LEAD_STATUS_LABELS[l.status]}</Badge>
            {l.lostReason ? (
              <span className="small muted">Motivo: {LOST_REASON_LABELS[l.lostReason]}</span>
            ) : null}
          </span>
        }
        actions={<Link to="/vendas">Voltar aos leads</Link>}
      />
      <div className="grid cols-2">
        <div className="stack">
          <Card title="Contato">
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate({
                  name: form.name || undefined,
                  phone: form.phone || undefined,
                  notes: form.notes || null,
                  planCode: form.planCode,
                });
              }}
            >
              {save.error ? <Alert kind="risk">{errorMessage(save.error)}</Alert> : null}
              <TextInput
                label="Nome"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <TextInput
                label="WhatsApp"
                inputMode="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <SelectInput
                label="Plano"
                value={form.planCode}
                onChange={(e) => setForm({ ...form, planCode: e.target.value })}
                options={PLAN_CODES.map((c) => ({
                  value: c,
                  label: `${PLANS[c].name} (${formatBRL(PLANS[c].priceCents)})`,
                }))}
              />
              <TextArea
                label="Observações"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
              <Button type="submit" loading={save.isPending}>
                Salvar
              </Button>
            </form>
            <div className="row" style={{ marginTop: 16 }}>
              {l.phone ? (
                <>
                  <LinkButton
                    variant="whatsapp"
                    href={whatsappUrl(l.phone)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Abrir WhatsApp
                  </LinkButton>
                  <LinkButton variant="secondary" href={`tel:+${l.phone}`}>
                    Ligar
                  </LinkButton>
                </>
              ) : null}
              {l.status !== "won" && l.status !== "lost" ? (
                <Button variant="ghost" onClick={() => setLosing(true)}>
                  Marcar como perdido
                </Button>
              ) : null}
              {l.status === "lost" || l.status === "no_reply" ? (
                <Button variant="ghost" onClick={() => save.mutate({ status: "in_conversation" })}>
                  Voltar para em conversa
                </Button>
              ) : null}
            </div>
          </Card>
          <Card title="Origem">
            <div className="stack small">
              <div>
                Plano clicado:{" "}
                <strong>{l.planCode ? PLANS[l.planCode].name : "não informado"}</strong>
              </div>
              <div>Campanha: {l.utmCampaign ?? "sem campanha"}</div>
              <div>
                Fonte: {l.utmSource ?? "direto"} {l.variant ? `· variante ${l.variant}` : ""}
              </div>
              <div>Clique: {dateTimeBR(l.createdAt)}</div>
            </div>
          </Card>
        </div>
        <div className="stack">
          <Card
            title="Link de matrícula"
            actions={
              l.status !== "won" && !activeLink ? (
                <Button onClick={() => setGenerating(true)} disabled={!l.name || !l.phone}>
                  Gerar link de matrícula
                </Button>
              ) : null
            }
          >
            {!l.name || !l.phone ? (
              <Alert kind="note">Registre nome e WhatsApp para gerar o link.</Alert>
            ) : null}
            {l.links.length === 0 ? <p className="muted">Nenhum link gerado ainda.</p> : null}
            <div className="stack">
              {[...l.links].reverse().map((k) => (
                <LinkCard key={k.id} leadId={l.id} link={k} />
              ))}
            </div>
          </Card>
          <Card title="Linha do tempo">
            <div className="stack small">
              {[...l.events].reverse().map((ev) => (
                <div key={ev.id} className="row between">
                  <span>{EVENT_LABELS[ev.type] ?? ev.type}</span>
                  <span className="muted">{dateTimeBR(ev.createdAt)}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
      {generating ? (
        <GenerateLinkModal
          leadId={l.id}
          leadName={l.name ?? ""}
          defaultPlan={(l.planCode ?? "IND-1H") as PlanCode}
          onClose={() => setGenerating(false)}
        />
      ) : null}
      {losing ? (
        <LostModal
          onClose={() => setLosing(false)}
          onConfirm={(reason) => {
            save.mutate({ status: "lost", lostReason: reason });
            setLosing(false);
          }}
        />
      ) : null}
    </>
  );
}

function LinkCard({ leadId, link }: { leadId: string; link: LeadLink }) {
  const qc = useQueryClient();
  const [alt, setAlt] = useState("");
  const change = useMutation({
    mutationFn: (slot: DayRange[]) =>
      api.patch(`/v1/leads/${leadId}/enrollment-links/${link.id}/slot`, { slot }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lead", leadId] }),
  });
  const s = LINK_STATUS[link.status];
  const open = link.status === "pending" || link.status === "opened" || link.status === "filled";
  return (
    <div className="card tight">
      <div className="row between">
        <strong>
          {link.purpose === "second_student"
            ? "Segundo aluno"
            : link.planCode
              ? PLANS[link.planCode].name
              : "Matrícula"}
        </strong>
        <Badge color={s.color}>{s.label}</Badge>
      </div>
      <div className="small" style={{ marginTop: 8 }}>
        Professor: <strong>{link.teacher?.name}</strong>
        <br />
        Horário: {link.slotLabel || "liberado"}
        <br />
        {open ? `Vale até ${link.expiresLabel}` : null}
      </div>
      {open && link.purpose === "primary" ? (
        <div className="stack" style={{ marginTop: 10 }}>
          <div className="copy-box">{link.message}</div>
          <div className="row">
            <CopyButton text={link.message} label="Copiar mensagem" />
            <CopyButton text={link.url} label="Copiar link" />
          </div>
          {link.alternatives.length > 0 && link.status !== "filled" ? (
            <div className="row" style={{ alignItems: "flex-end" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <SelectInput
                  label="Trocar horário (mesmo professor)"
                  value={alt}
                  onChange={(e) => setAlt(e.target.value)}
                  options={[
                    { value: "", label: "Escolha uma alternativa" },
                    ...link.alternatives.map((a, i) => ({ value: String(i), label: a.label })),
                  ]}
                />
              </div>
              <Button
                variant="secondary"
                disabled={alt === ""}
                loading={change.isPending}
                onClick={() => {
                  const a = link.alternatives[Number(alt)];
                  if (a) change.mutate(a.slot);
                }}
              >
                Trocar
              </Button>
            </div>
          ) : null}
          {change.error ? <Alert kind="risk">{errorMessage(change.error)}</Alert> : null}
        </div>
      ) : null}
    </div>
  );
}

function LostModal({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: (r: LostReason) => void;
}) {
  const [reason, setReason] = useState<LostReason>("price");
  return (
    <Modal title="Marcar como perdido" onClose={onClose}>
      <div className="stack">
        <SelectInput
          label="Motivo"
          value={reason}
          onChange={(e) => setReason(e.target.value as LostReason)}
          options={LOST_REASONS.map((r) => ({ value: r, label: LOST_REASON_LABELS[r] }))}
        />
        <Button variant="danger" onClick={() => onConfirm(reason)}>
          Confirmar perda
        </Button>
      </div>
    </Modal>
  );
}

interface GenerateResult {
  enrollmentId: string;
  link: { id: string; url: string; expiresAt: string; message: string };
  teacher: { id: string; name: string };
  slot: DayRange[];
  slotLabel: string;
  alternatives: { slot: DayRange[]; label: string }[];
}

/** Gerar link de matrícula: plano e modalidade, janelas do aluno e o professor da vez. */
function GenerateLinkModal({
  leadId,
  leadName,
  defaultPlan,
  onClose,
}: {
  leadId: string;
  leadName: string;
  defaultPlan: PlanCode;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [planCode, setPlanCode] = useState<PlanCode>(defaultPlan);
  const [mode, setMode] = useState<"each_pays" | "single_payer">("each_pays");
  const [names, setNames] = useState<[string, string]>([leadName, ""]);
  const [windows, setWindows] = useState<DayRange[]>([]);
  const [preferred, setPreferred] = useState<DayRange[]>([]);
  const [paintPref, setPaintPref] = useState(false);
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [alt, setAlt] = useState("");
  const plan = PLANS[planCode];

  const gen = useMutation({
    mutationFn: () =>
      api.post<GenerateResult>(`/v1/leads/${leadId}/enrollment-links`, {
        planCode,
        mode: plan.isDuo ? mode : "individual",
        studentNames: plan.isDuo ? names : [names[0]],
        windows,
        preferred,
      }),
    onSuccess: (r) => {
      setResult(r);
      setStep(3);
      qc.invalidateQueries({ queryKey: ["lead", leadId] });
    },
  });
  const change = useMutation({
    mutationFn: (slot: DayRange[]) =>
      api.patch<{ slotLabel: string }>(
        `/v1/leads/${leadId}/enrollment-links/${result?.link.id}/slot`,
        { slot },
      ),
    onSuccess: (r, slot) => {
      if (result) setResult({ ...result, slot, slotLabel: r.slotLabel });
      qc.invalidateQueries({ queryKey: ["lead", leadId] });
    },
  });

  return (
    <Modal title="Gerar link de matrícula" onClose={onClose}>
      <div className="steps" style={{ marginBottom: 16 }}>
        <span className="done" />
        <span className={step >= 2 ? "done" : ""} />
        <span className={step >= 3 ? "done" : ""} />
      </div>
      {step === 1 ? (
        <div className="stack">
          <h3>1. Plano e modalidade</h3>
          <Chips
            value={planCode}
            onChange={setPlanCode}
            options={PLAN_CODES.map((c) => ({
              value: c,
              label: `${PLANS[c].name} ${formatBRL(PLANS[c].priceCents)}`,
            }))}
          />
          {plan.isDuo ? (
            <>
              <Chips
                value={mode}
                onChange={setMode}
                options={[
                  { value: "each_pays", label: "Cada um paga (dois links)" },
                  {
                    value: "single_payer",
                    label: `Um pagador (${formatBRL(plan.priceCents * 2)})`,
                  },
                ]}
              />
              <TextInput
                label="Nome do primeiro aluno"
                value={names[0]}
                onChange={(e) => setNames([e.target.value, names[1]])}
              />
              <TextInput
                label="Nome do segundo aluno"
                value={names[1]}
                onChange={(e) => setNames([names[0], e.target.value])}
              />
              <p className="small muted">A dupla usa o mesmo horário.</p>
            </>
          ) : null}
          <Button
            block
            disabled={plan.isDuo && (!names[0].trim() || !names[1].trim())}
            onClick={() => setStep(2)}
          >
            Continuar
          </Button>
        </div>
      ) : null}
      {step === 2 ? (
        <div className="stack">
          <h3>2. Janelas do aluno</h3>
          <p className="small muted">
            Marque os dias e horários em que o aluno pode (
            {plan.daysPerWeek === 2
              ? "duas aulas de 1h em dias diferentes"
              : `aula de ${plan.weeklyMinutes / 60}h`}
            ). Depois marque até três faixas preferidas.
          </p>
          <Chips
            value={paintPref ? "pref" : "win"}
            onChange={(v) => setPaintPref(v === "pref")}
            options={[
              { value: "win", label: "Marcar janelas" },
              { value: "pref", label: "Marcar preferidas" },
            ]}
          />
          <WeekGrid
            value={windows}
            onChange={setWindows}
            preferred={preferred}
            onPreferredChange={setPreferred}
            paintPreferred={paintPref}
          />
          {preferred.length > 3 ? (
            <Alert kind="warning">Marque no máximo três faixas preferidas.</Alert>
          ) : null}
          {gen.error ? <Alert kind="risk">{errorMessage(gen.error)}</Alert> : null}
          <div className="row">
            <Button variant="secondary" onClick={() => setStep(1)}>
              Voltar
            </Button>
            <Button
              disabled={windows.length === 0 || preferred.length > 3}
              loading={gen.isPending}
              onClick={() => gen.mutate()}
            >
              Encontrar professor
            </Button>
          </div>
        </div>
      ) : null}
      {step === 3 && result ? (
        <div className="stack">
          <h3>3. Link pronto</h3>
          <Alert kind="success" title={`Professor da vez: ${result.teacher.name}`}>
            Horário reservado por 48 horas: {result.slotLabel}
          </Alert>
          {result.alternatives.length > 0 ? (
            <div className="row" style={{ alignItems: "flex-end" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <SelectInput
                  label="Trocar horário (mesmo professor)"
                  value={alt}
                  onChange={(e) => setAlt(e.target.value)}
                  options={[
                    { value: "", label: "Manter o sugerido" },
                    ...result.alternatives.map((a, i) => ({ value: String(i), label: a.label })),
                  ]}
                />
              </div>
              <Button
                variant="secondary"
                disabled={alt === ""}
                loading={change.isPending}
                onClick={() => {
                  const a = result.alternatives[Number(alt)];
                  if (a) change.mutate(a.slot);
                }}
              >
                Trocar
              </Button>
            </div>
          ) : null}
          {change.error ? <Alert kind="risk">{errorMessage(change.error)}</Alert> : null}
          <div className="copy-box">{result.link.message}</div>
          <div className="row">
            <CopyButton text={result.link.message} label="Copiar" />
            <Button variant="ghost" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
